//@flow
import {
  endpoints,
  createChatbotApi,
  deleteChatbotApi,
  editChatbotApi,
  getChatbotApi,
  trainChatbotApi,
  getTrainingApi,
  deleteTrainingApi,
  listChatbotsApi,
} from './api';
import {buildRoute} from '../../http';
import {getFileInfo} from '../file-storage/utils';

import type {
  PaginationRequest,
  StoreChatbot,
  PaginationResponse,
  ChatbotTraining,
  ChatbotReplySchedule,
  TrainedMessageAttachment,
  TrainedMessage,
  FileInfoResponse,
} from '../../entities';

export function createChatbotService(name: string, prompt: string) {
  return createChatbotApi({
    name,
    enabled: true,
    prompt,
  }).toPromise();
}

export function listChatbotsService(
  params?: PaginationRequest = {size: 12, page: 0}
): Promise<PaginationResponse<StoreChatbot>> {
  return listChatbotsApi(params).toPromise();
}

export function deleteChatbotService(chatbotId: string) {
  const route = buildRoute(endpoints.DELETE_CHATBOT, [chatbotId]);

  return deleteChatbotApi({route}).toPromise();
}

export type EditChatbotServiceParams = {
  name?: string,
  enabled?: boolean,
  prompt?: string,
  devices?: {
    id: string,
    ravenService: string,
  }[],
  replySchedule?: ChatbotReplySchedule,
};

export function editChatbotService(
  chatbotId: string,
  params: EditChatbotServiceParams
) {
  const route = buildRoute(endpoints.EDIT_CHATBOT, [chatbotId]);

  return editChatbotApi(params, {route}).toPromise();
}

export async function getChatbotService(
  chatbotId: string
): Promise<StoreChatbot> {
  const route = buildRoute(endpoints.GET_CHATBOT, [chatbotId]);

  return getChatbotApi(undefined, {route}).toPromise();
}

export type TrainedMessageRequest = {
  question: string,
  answer: string,
  attachments?: TrainedMessageAttachment[],
};

type TrainingData = {
  trainedMessages?: TrainedMessageRequest[],
  text?: string,
  pdfFiles?: string[],
};

export type TrainChatBotServiceRequest = {
  trainingType:
    | 'STORE_INFORMATION'
    | 'TEXT'
    | 'PDF'
    | 'TRAINED_MESSAGES'
    | 'PRODUCTS',
  trainingData: TrainingData,
};

export function trainChatbotService(
  chatbotId: string,
  data: TrainChatBotServiceRequest
) {
  const route = buildRoute(endpoints.TRAIN_CHATBOT, [chatbotId]);

  return trainChatbotApi(data, {route}).toPromise();
}

type GetTrainingServiceRequest = PaginationRequest;

/**
 * Get attachment type from resourceType
 */
function getAttachmentTypeFromResourceType(
  resourceType: string
): 'document' | 'image' | 'video' | 'audio' {
  const type = resourceType.toLowerCase();
  if (type === 'image' || type === 'jpg' || type === 'png' || type === 'gif') {
    return 'image';
  }
  if (type === 'video' || type === 'mp4') {
    return 'video';
  }
  if (type === 'audio' || type === 'mp3') {
    return 'audio';
  }
  return 'document';
}

/**
 * Convert FileInfoResponse to TrainedMessageAttachment
 */
function fileInfoToAttachment(
  fileInfo: FileInfoResponse
): TrainedMessageAttachment {
  return {
    file_id: fileInfo.id,
    type: getAttachmentTypeFromResourceType(fileInfo.resourceType),
    url: fileInfo.url,
    name: fileInfo.metadata?.originalFilename || '',
    mime_type: fileInfo.metadata?.extension
      ? `application/${fileInfo.metadata.extension}`
      : '',
  };
}

/**
 * Adapter to normalize legacy training messages (with file_id) to new format (with attachments)
 * Fetches full file info for legacy file_ids to populate attachment data
 */
async function adaptTrainedMessage(
  message: TrainedMessage
): Promise<TrainedMessage> {
  // If already has attachments, return as-is
  if (message.attachments && message.attachments.length > 0) {
    return message;
  }

  // If has legacy file_id, fetch file info and convert to attachments array
  if (message.file_id) {
    const fileInfo = await getFileInfo(message.file_id);

    if (fileInfo) {
      const attachment = fileInfoToAttachment(fileInfo);
      return {
        question: message.question,
        answer: message.answer,
        attachments: [attachment],
      };
    }

    // Fallback if file info fetch fails - create minimal attachment
    return {
      question: message.question,
      answer: message.answer,
      attachments: [
        {
          file_id: message.file_id,
          type: 'document',
          url: '',
          name: '',
          mime_type: '',
        },
      ],
    };
  }

  return message;
}

/**
 * Adapter to normalize ChatbotTraining data from API response
 * Handles backward compatibility for TRAINED_MESSAGES type
 */
async function adaptChatbotTraining(
  training: ChatbotTraining
): Promise<ChatbotTraining> {
  if (training.trainingType !== 'TRAINED_MESSAGES') {
    return training;
  }

  const trainedMessages = training.metadata?.values?.TRAINED_MESSAGES;
  if (!trainedMessages || !Array.isArray(trainedMessages)) {
    return training;
  }

  const adaptedMessages = await Promise.all(
    trainedMessages.map(adaptTrainedMessage)
  );

  return {
    ...training,
    metadata: {
      ...training.metadata,
      values: {
        ...training.metadata.values,
        TRAINED_MESSAGES: adaptedMessages,
      },
    },
  };
}

export async function getTrainingService(
  chatbotId: string,
  request: GetTrainingServiceRequest
): Promise<PaginationResponse<ChatbotTraining>> {
  const route = buildRoute(endpoints.GET_TRAINING, [chatbotId]);

  const response = await getTrainingApi(request, {route}).toPromise();

  // Apply adapter to normalize legacy data
  const adaptedData = await Promise.all(
    response.data.map(adaptChatbotTraining)
  );

  return {
    ...response,
    data: adaptedData,
  };
}

export function deleteTrainingService(trainingId: string) {
  const route = buildRoute(endpoints.DELETE_TRAINING, [trainingId]);

  return deleteTrainingApi({route}).toPromise();
}
